import { useCallback, useEffect, useRef, useState } from 'react';
import { getRecordingBlob } from '@/api/calls';

export interface PlayerState {
  loading: boolean;
  error: string | null;
  playing: boolean;
  /** Секунды. */
  time: number;
  duration: number;
}

/**
 * Проигрыватель записи разговора. Файл берётся с пропуском (хранилище закрыто, у тега audio нет
 * заголовков) — один раз, по первому нажатию, и играет из памяти телефона; закрыли окно — память освобождена.
 * `seek(sec)` из строки расшифровки сам загрузит запись, если её ещё нет.
 */
export function useRecordingPlayer(callId: string, fallbackDuration: number) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const url = useRef<string | null>(null);
  const loading = useRef<Promise<HTMLAudioElement | null> | null>(null);
  const [st, setSt] = useState<PlayerState>({ loading: false, error: null, playing: false, time: 0, duration: fallbackDuration });

  useEffect(() => () => {
    audio.current?.pause();
    if (url.current) URL.revokeObjectURL(url.current);
    audio.current = null; url.current = null; loading.current = null;
  }, [callId]);

  const ensure = useCallback(async (): Promise<HTMLAudioElement | null> => {
    if (audio.current) return audio.current;
    if (loading.current) return loading.current;
    setSt((s) => ({ ...s, loading: true, error: null }));
    loading.current = (async () => {
      try {
        const blob = await getRecordingBlob(callId);
        const u = URL.createObjectURL(blob);
        url.current = u;
        const a = new Audio(u);
        a.preload = 'auto';
        a.addEventListener('timeupdate', () => setSt((s) => ({ ...s, time: a.currentTime })));
        a.addEventListener('durationchange', () => {
          if (Number.isFinite(a.duration) && a.duration > 0) setSt((s) => ({ ...s, duration: a.duration }));
        });
        a.addEventListener('play', () => setSt((s) => ({ ...s, playing: true })));
        a.addEventListener('pause', () => setSt((s) => ({ ...s, playing: false })));
        a.addEventListener('ended', () => setSt((s) => ({ ...s, playing: false })));
        a.addEventListener('error', () => setSt((s) => ({ ...s, playing: false, error: 'Запись не воспроизводится на этом телефоне' })));
        audio.current = a;
        setSt((s) => ({ ...s, loading: false }));
        return a;
      } catch (e) {
        loading.current = null;
        setSt((s) => ({ ...s, loading: false, error: e instanceof Error ? e.message : 'Не удалось загрузить запись' }));
        return null;
      }
    })();
    return loading.current;
  }, [callId]);

  const toggle = useCallback(async () => {
    const a = await ensure();
    if (!a) return;
    if (a.paused) await a.play().catch(() => undefined);
    else a.pause();
  }, [ensure]);

  const seek = useCallback(async (sec: number, play = true) => {
    const a = await ensure();
    if (!a) return;
    a.currentTime = Math.max(0, sec);
    setSt((s) => ({ ...s, time: a.currentTime }));
    if (play && a.paused) await a.play().catch(() => undefined);
  }, [ensure]);

  return { ...st, toggle, seek };
}
